import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Space,
  Button,
  Empty,
  message,
} from 'antd';
import {
  DashboardOutlined,
  AlertOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  FilterOutlined,
  HistoryOutlined,
  UserSwitchOutlined,
  BlockOutlined,
  BarChartOutlined,
  PieChartOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  BookOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { actionApi } from '../../utils/actionApi';
import { StatusBadge, PriorityBadge, ProgressBadge } from '../../components/action';
import { CardListSkeleton } from '../../components/SkeletonLoaders';

const { Title, Text } = Typography;

const ActionDashboard = () => {
  const navigate = useNavigate();
  const { actionToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [inbox, setInbox] = useState({
    needs_acceptance: [],
    today: [],
    overdue: [],
    due_soon: [],
    high_priority: [],
    recent_updates: [],
  });
  const [bookmarks, setBookmarks] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const [shortcuts, setShortcuts] = useState({
    recently_updated: [],
    waiting_on_others: [],
    blocked: [],
  });
  const [activityFeed, setActivityFeed] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    if (actionToken) {
      fetchDashboard();
      
      // آپدیت خودکار هر یک دقیقه
      const interval = setInterval(() => {
        fetchDashboard();
      }, 60000); // 60000 میلی‌ثانیه = 1 دقیقه
      
      return () => clearInterval(interval);
    }
  }, [actionToken]);

  const fetchDashboard = async () => {
    if (!actionToken) return;

    setLoading(true);
    try {
      const res = await actionApi.getActionDashboard();
      const data = res?.data || {};
      setStats(data.stats || null);
      setInbox({
        needs_acceptance: data.inbox?.needs_acceptance || [],
        today: data.inbox?.today || [],
        overdue: data.inbox?.overdue || [],
        due_soon: data.inbox?.due_soon || [],
        high_priority: data.inbox?.high_priority || [],
        recent_updates: data.inbox?.recent_updates || [],
      });
      setBookmarks(data.bookmarks || []);
      setSavedFilters(data.saved_filters || []);
      setShortcuts({
        recently_updated: data.shortcuts?.recently_updated || [],
        waiting_on_others: data.shortcuts?.waiting_on_others || [],
        blocked: data.shortcuts?.blocked || [],
      });
      setActivityFeed(data.activity_feed || []);
      setGeneratedAt(data.meta?.generated_at || null);
    } catch (error) {
      console.error('Load dashboard error:', error);
      message.error(error.message || 'خطا در دریافت داده‌های داشبورد اقدام');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAction = (id) => {
    if (!id) return;
    navigate(`/action/${id}`);
  };

  const renderInboxSection = (title, icon, items, accent) => {
    const accentClasses =
      accent === 'warning'
        ? 'bg-amber-50 border-amber-100'
        : accent === 'danger'
        ? 'bg-rose-50 border-rose-100'
        : accent === 'info'
        ? 'bg-blue-50 border-blue-100'
        : 'bg-emerald-50 border-emerald-100';

    const iconBg =
      accent === 'warning'
        ? 'bg-amber-400'
        : accent === 'danger'
        ? 'bg-rose-500'
        : accent === 'info'
        ? 'bg-blue-500'
        : 'bg-emerald-500';

    return (
      <Card
        size="small"
        style={{ borderRadius: 16 }}
        bodyStyle={{ padding: 14 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-white ${iconBg}`}>
              {icon}
            </div>
            <span
              className="text-sm font-medium text-gray-800 dark:text-gray-100"
              style={{ fontFamily: 'CustomFont' }}
            >
              {title}
            </span>
          </div>
          <Tag
            color="default"
            style={{ fontFamily: 'CustomFont', fontSize: 11, borderRadius: 999 }}
          >
            {items?.length || 0} مورد
          </Tag>
        </div>

        {(!items || items.length === 0) ? (
          <div
            className={`rounded-xl border text-xs text-gray-500 dark:text-gray-400 px-3 py-3 flex items-center justify-between ${accentClasses}`}
            style={{ fontFamily: 'CustomFont' }}
          >
            <span>موردی در این بخش وجود ندارد</span>
          </div>
        ) : (
          <div className="space-y-2.5 max-h-80 overflow-auto pr-1">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleOpenAction(item.id)}
                className="w-full text-right"
              >
                <div
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 flex flex-col gap-1.5 hover:border-emerald-400 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="text-[11px] text-gray-400 dark:text-gray-500"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {item.number}
                        </span>
                        {bookmarks.includes(item.id) && (
                          <span className="inline-flex w-1.5 h-1.5 rounded-full bg-amber-400" />
                        )}
                      </div>
                      <span
                        className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {item.title}
                      </span>
                    </div>
                    <ArrowLeftOutlined className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  </div>

                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    {item.priority && (
                      <PriorityBadge priority={item.priority} size="small" />
                    )}
                    {item.status && (
                      <StatusBadge status={item.status} size="small" />
                    )}
                    {typeof item.percent_complete === 'number' && (
                      <ProgressBadge
                        percent={item.percent_complete}
                        size="small"
                      />
                    )}
                  </div>

                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span
                      className="text-[11px] text-gray-500 dark:text-gray-400"
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      موعد برنامه‌ریزی‌شده:{' '}
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        {item.planned_end_date || '-'}
                      </span>
                    </span>
                    {Array.isArray(item.raci_roles) && item.raci_roles.length > 0 && (
                      <Space size={4} wrap>
                        {item.raci_roles.map((role) => (
                          <Tag
                            key={role}
                            color="blue"
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 10,
                              borderRadius: 999,
                              paddingInline: 8,
                            }}
                          >
                            {role}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    );
  };

  const totalOpen = stats?.open || 0;
  const totalInProgress = stats?.in_progress || 0;
  const totalClosed = stats?.closed || 0;
  const totalOverdue = stats?.overdue || 0;
  const totalDueSoon = stats?.due_soon || 0;
  const totalToday = stats?.today || 0;
  const cumulativeProgress = Number(stats?.cumulative_progress_pct || 0);

  return (
    <MainLayout>
      <div className="px-4 md:px-6 py-5 w-full">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-400 via-sky-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
                  <DashboardOutlined className="text-white text-xl" />
                </div>
              </div>
              <div>
                <h1
                  className="text-2xl font-bold text-gray-900 dark:text-white"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  داشبورد اقدام
                </h1>
                {generatedAt && (
                  <p
                    className="text-sm text-gray-500 dark:text-gray-400 mt-0.5"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    آخرین به‌روزرسانی: {generatedAt}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchDashboard}
                className="px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center gap-2 transition-colors"
                style={{ fontFamily: 'CustomFont' }}
              >
                <ClockCircleOutlined className="text-sm" />
                به‌روزرسانی داشبورد
              </button>
              <button
                onClick={() => navigate('/action/my-actions')}
                className="px-4 py-2 text-sm rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 transition-all"
                style={{ fontFamily: 'CustomFont' }}
              >
                <ArrowLeftOutlined className="text-xs" />
                لیست اقدام‌های من
              </button>
            </div>
          </div>

          {/* Stats */}
          {loading && !stats && (
            <div className="mt-4">
              <CardListSkeleton count={3} />
            </div>
          )}

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
              <Card
                size="small"
                style={{ borderRadius: 12, background: '#eff6ff' }}
                bodyStyle={{ padding: 12 }}
              >
                <Statistic
                  title={
                    <span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                      باز
                    </span>
                  }
                  value={totalOpen}
                  valueStyle={{
                    fontSize: 18,
                    fontFamily: 'CustomFont',
                    color: '#2563eb',
                  }}
                />
              </Card>
              <Card
                size="small"
                style={{ borderRadius: 12, background: '#ecfeff' }}
                bodyStyle={{ padding: 12 }}
              >
                <Statistic
                  title={
                    <span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                      در حال انجام
                    </span>
                  }
                  value={totalInProgress}
                  valueStyle={{
                    fontSize: 18,
                    fontFamily: 'CustomFont',
                    color: '#0ea5e9',
                  }}
                />
              </Card>
              <Card
                size="small"
                style={{ borderRadius: 12, background: '#ecfdf5' }}
                bodyStyle={{ padding: 12 }}
              >
                <Statistic
                  title={
                    <span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                      بسته شده
                    </span>
                  }
                  value={totalClosed}
                  valueStyle={{
                    fontSize: 18,
                    fontFamily: 'CustomFont',
                    color: '#10b981',
                  }}
                />
              </Card>
              <Card
                size="small"
                style={{ borderRadius: 12, background: '#fef3c7' }}
                bodyStyle={{ padding: 12 }}
              >
                <Statistic
                  title={
                    <span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                      موعد امروز
                    </span>
                  }
                  value={totalToday}
                  valueStyle={{
                    fontSize: 18,
                    fontFamily: 'CustomFont',
                    color: '#f59e0b',
                  }}
                />
              </Card>
              <Card
                size="small"
                style={{ borderRadius: 12, background: '#fee2e2' }}
                bodyStyle={{ padding: 12 }}
              >
                <Statistic
                  title={
                    <span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                      تأخیر خورده
                    </span>
                  }
                  value={totalOverdue}
                  valueStyle={{
                    fontSize: 18,
                    fontFamily: 'CustomFont',
                    color: '#ef4444',
                  }}
                />
              </Card>
              <Card
                size="small"
                style={{ borderRadius: 12, background: '#f5f3ff' }}
                bodyStyle={{ padding: 12 }}
              >
                <Statistic
                  title={
                    <span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                      نزدیک موعد
                    </span>
                  }
                  value={totalDueSoon}
                  valueStyle={{
                    fontSize: 18,
                    fontFamily: 'CustomFont',
                    color: '#8b5cf6',
                  }}
                />
              </Card>
              <Card
                size="small"
                style={{ borderRadius: 12, background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' }}
                bodyStyle={{ padding: 10 }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Text
                      style={{
                        fontFamily: 'CustomFont',
                        fontSize: 11,
                        color: '#059669',
                      }}
                    >
                      میانگین پیشرفت
                    </Text>
                    <div className="mt-1">
                      <Text
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 18,
                          fontWeight: 600,
                          color: '#10b981',
                        }}
                      >
                        {cumulativeProgress.toFixed(1)}٪
                      </Text>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Progress
                      type="circle"
                      percent={Math.round(cumulativeProgress)}
                      width={46}
                      strokeColor="#10b981"
                      trailColor="#d1fae5"
                    />
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* Inbox sections */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {renderInboxSection(
            'نیاز به پذیرش شما',
            <AlertOutlined />,
            inbox.needs_acceptance || [],
            'warning'
          )}
          {renderInboxSection(
            'اقدامات با موعد امروز',
            <ClockCircleOutlined />,
            inbox.today || [],
            'info'
          )}
          {renderInboxSection(
            'اقدامات تأخیر خورده',
            <AlertOutlined />,
            inbox.overdue || [],
            'danger'
          )}
        </div>

        <div className="mt-4">
          {renderInboxSection(
            'اقدامات نزدیک به موعد',
            <ThunderboltOutlined />,
            inbox.due_soon || [],
            'success'
          )}
        </div>

        {/* بخش‌های اضافی */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {renderInboxSection(
            'اولویت بالا',
            <AlertOutlined />,
            inbox.high_priority || [],
            'danger'
          )}
          {renderInboxSection(
            'به‌روزرسانی‌های اخیر',
            <HistoryOutlined />,
            inbox.recent_updates || [],
            'info'
          )}
        </div>

        {/* Shortcuts */}
        {(shortcuts.recently_updated?.length > 0 ||
          shortcuts.waiting_on_others?.length > 0 ||
          shortcuts.blocked?.length > 0) && (
          <div className="mt-6">
            <Title level={4} style={{ fontFamily: 'CustomFont', marginBottom: 16 }}>
              میانبرها
            </Title>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {renderInboxSection(
                'به‌روزرسانی‌های اخیر',
                <HistoryOutlined />,
                shortcuts.recently_updated || [],
                'info'
              )}
              {renderInboxSection(
                'در انتظار دیگران',
                <UserSwitchOutlined />,
                shortcuts.waiting_on_others || [],
                'warning'
              )}
              {renderInboxSection(
                'مسدود شده',
                <BlockOutlined />,
                shortcuts.blocked || [],
                'danger'
              )}
            </div>
          </div>
        )}

        {/* Bookmarks */}
        {bookmarks.length > 0 && (
          <div className="mt-6">
            <Title level={4} style={{ fontFamily: 'CustomFont', marginBottom: 16 }}>
              نشان‌ها ({bookmarks.length})
            </Title>
            {renderInboxSection(
              'اقدامات نشان شده',
              <StarOutlined />,
              bookmarks,
              'warning'
            )}
          </div>
        )}

        {/* Saved Filters */}
        {savedFilters.length > 0 && (
          <div className="mt-6">
            <Title level={4} style={{ fontFamily: 'CustomFont', marginBottom: 16 }}>
              فیلترهای ذخیره شده ({savedFilters.length})
            </Title>
            <Card style={{ borderRadius: 16 }}>
              <div className="space-y-2">
                {savedFilters.map((filter, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    onClick={() => {
                      // می‌توانید به صفحه فیلتر شده هدایت کنید
                      navigate('/action/my-actions', { state: { filterId: filter.id } });
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <FilterOutlined className="text-blue-500" />
                      <span
                        className="text-sm font-medium text-gray-900 dark:text-white"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {filter.name || `فیلتر ${index + 1}`}
                      </span>
                    </div>
                    <ArrowLeftOutlined className="text-gray-400" />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* آمار تفکیکی */}
        {stats && (
          <div className="mt-6">
            <Title level={4} style={{ fontFamily: 'CustomFont', marginBottom: 16 }}>
              آمار تفکیکی
            </Title>
            
            {/* توزیع بر اساس اولویت */}
            {stats.by_priority && Object.keys(stats.by_priority).length > 0 && (
              <Card className="mb-4" style={{ borderRadius: 16 }}>
                <div className="flex items-center gap-2 mb-4">
                  <BarChartOutlined className="text-blue-500" />
                  <Title level={5} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                    توزیع بر اساس اولویت
                  </Title>
                </div>
                <Row gutter={[16, 16]}>
                  {Object.entries(stats.by_priority).map(([priority, count]) => {
                    const priorityColors = {
                      فوری_و_اضطراری: { color: '#ef4444', bg: '#fee2e2' },
                      بسیار_مهم: { color: '#f97316', bg: '#ffedd5' },
                      مهم: { color: '#3b82f6', bg: '#dbeafe' },
                      کم_اهمیت: { color: '#10b981', bg: '#d1fae5' },
                      غیرضروری: { color: '#6b7280', bg: '#f3f4f6' },
                    };
                    const priorityInfo = priorityColors[priority] || { color: '#6b7280', bg: '#f3f4f6' };
                    const priorityLabel =
                      priority === 'فوری_و_اضطراری'
                        ? 'فوری و اضطراری'
                        : priority === 'بسیار_مهم'
                        ? 'بسیار مهم'
                        : priority === 'کم_اهمیت'
                        ? 'کم اهمیت'
                        : priority;

                    return (
                      <Col xs={12} sm={8} md={6} key={priority}>
                        <div
                          style={{
                            padding: 12,
                            borderRadius: 12,
                            background: priorityInfo.bg,
                            border: `1px solid ${priorityInfo.color}20`,
                          }}
                        >
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 11,
                              color: '#6b7280',
                              marginBottom: 4,
                            }}
                          >
                            {priorityLabel}
                          </div>
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 20,
                              fontWeight: 700,
                              color: priorityInfo.color,
                            }}
                          >
                            {count}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            )}

            {/* توزیع بر اساس نوع اقدام */}
            {stats.by_type && Object.keys(stats.by_type).length > 0 && (
              <Card className="mb-4" style={{ borderRadius: 16 }}>
                <div className="flex items-center gap-2 mb-4">
                  <PieChartOutlined className="text-purple-500" />
                  <Title level={5} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                    توزیع بر اساس نوع اقدام
                  </Title>
                </div>
                <Row gutter={[16, 16]}>
                  {Object.entries(stats.by_type).map(([type, count]) => {
                    const typeColors = {
                      اصلاح: { color: '#8b5cf6', bg: '#f3e8ff' },
                      اقدام_پیشگیرانه: { color: '#10b981', bg: '#d1fae5' },
                      اقدام_اصلاحی: { color: '#3b82f6', bg: '#dbeafe' },
                    };
                    const typeInfo = typeColors[type] || { color: '#6b7280', bg: '#f3f4f6' };
                    const typeLabel =
                      type === 'اقدام_پیشگیرانه'
                        ? 'اقدام پیشگیرانه'
                        : type === 'اقدام_اصلاحی'
                        ? 'اقدام اصلاحی'
                        : type;

                    return (
                      <Col xs={12} sm={8} key={type}>
                        <div
                          style={{
                            padding: 12,
                            borderRadius: 12,
                            background: typeInfo.bg,
                            border: `1px solid ${typeInfo.color}20`,
                          }}
                        >
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 11,
                              color: '#6b7280',
                              marginBottom: 4,
                            }}
                          >
                            {typeLabel}
                          </div>
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 20,
                              fontWeight: 700,
                              color: typeInfo.color,
                            }}
                          >
                            {count}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            )}

            {/* توزیع پیشرفت */}
            {stats.progress_distribution && Object.keys(stats.progress_distribution).length > 0 && (
              <Card className="mb-4" style={{ borderRadius: 16 }}>
                <div className="flex items-center gap-2 mb-4">
                  <BarChartOutlined className="text-green-500" />
                  <Title level={5} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                    توزیع پیشرفت
                  </Title>
                </div>
                <Row gutter={[16, 16]}>
                  {Object.entries(stats.progress_distribution).map(([range, count]) => {
                    const rangeLabels = {
                      '0_25': '0-25%',
                      '26_50': '26-50%',
                      '51_75': '51-75%',
                      '76_99': '76-99%',
                      '100': '100%',
                    };
                    const rangeColors = {
                      '0_25': { color: '#ef4444', bg: '#fee2e2' },
                      '26_50': { color: '#f97316', bg: '#ffedd5' },
                      '51_75': { color: '#eab308', bg: '#fef9c3' },
                      '76_99': { color: '#10b981', bg: '#d1fae5' },
                      '100': { color: '#059669', bg: '#a7f3d0' },
                    };
                    const rangeInfo = rangeColors[range] || { color: '#6b7280', bg: '#f3f4f6' };

                    return (
                      <Col xs={12} sm={8} md={6} key={range}>
                        <div
                          style={{
                            padding: 12,
                            borderRadius: 12,
                            background: rangeInfo.bg,
                            border: `1px solid ${rangeInfo.color}20`,
                          }}
                        >
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 11,
                              color: '#6b7280',
                              marginBottom: 4,
                            }}
                          >
                            {rangeLabels[range] || range}
                          </div>
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 20,
                              fontWeight: 700,
                              color: rangeInfo.color,
                            }}
                          >
                            {count}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            )}

            {/* بار کاری */}
            {stats.workload && Object.keys(stats.workload).length > 0 && (
              <Card className="mb-4" style={{ borderRadius: 16 }}>
                <div className="flex items-center gap-2 mb-4">
                  <ClockCircleOutlined className="text-orange-500" />
                  <Title level={5} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                    بار کاری
                  </Title>
                </div>
                <Row gutter={[16, 16]}>
                  {Object.entries(stats.workload).map(([period, count]) => {
                    const periodLabels = {
                      this_week: 'این هفته',
                      next_week: 'هفته آینده',
                      later: 'بعداً',
                      no_due: 'بدون موعد',
                    };
                    const periodColors = {
                      this_week: { color: '#ef4444', bg: '#fee2e2' },
                      next_week: { color: '#f97316', bg: '#ffedd5' },
                      later: { color: '#3b82f6', bg: '#dbeafe' },
                      no_due: { color: '#6b7280', bg: '#f3f4f6' },
                    };
                    const periodInfo = periodColors[period] || { color: '#6b7280', bg: '#f3f4f6' };

                    return (
                      <Col xs={12} sm={6} key={period}>
                        <div
                          style={{
                            padding: 12,
                            borderRadius: 12,
                            background: periodInfo.bg,
                            border: `1px solid ${periodInfo.color}20`,
                          }}
                        >
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 11,
                              color: '#6b7280',
                              marginBottom: 4,
                            }}
                          >
                            {periodLabels[period] || period}
                          </div>
                          <div
                            style={{
                              fontFamily: 'CustomFont',
                              fontSize: 20,
                              fontWeight: 700,
                              color: periodInfo.color,
                            }}
                          >
                            {count}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            )}

            {/* SLA */}
            {stats.sla && (
              <Card className="mb-4" style={{ borderRadius: 16 }}>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircleOutlined className="text-green-500" />
                  <Title level={5} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                    وضعیت SLA
                  </Title>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: '#d1fae5',
                        border: '1px solid #10b98120',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 11,
                          color: '#6b7280',
                          marginBottom: 4,
                        }}
                      >
                        در SLA
                      </div>
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 20,
                          fontWeight: 700,
                          color: '#10b981',
                        }}
                      >
                        {stats.sla.in_sla || 0}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: '#fee2e2',
                        border: '1px solid #ef444420',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 11,
                          color: '#6b7280',
                          marginBottom: 4,
                        }}
                      >
                        خارج از SLA
                      </div>
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 20,
                          fontWeight: 700,
                          color: '#ef4444',
                        }}
                      >
                        {stats.sla.out_sla || 0}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: '#f3f4f6',
                        border: '1px solid #6b728020',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 11,
                          color: '#6b7280',
                          marginBottom: 4,
                        }}
                      >
                        میانگین تأخیر (روز)
                      </div>
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 20,
                          fontWeight: 700,
                          color: '#6b7280',
                        }}
                      >
                        {stats.sla.avg_delay_days || 0}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: '#f3f4f6',
                        border: '1px solid #6b728020',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 11,
                          color: '#6b7280',
                          marginBottom: 4,
                        }}
                      >
                        حداکثر تأخیر (روز)
                      </div>
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 20,
                          fontWeight: 700,
                          color: '#6b7280',
                        }}
                      >
                        {stats.sla.max_delay_days || 0}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            )}

            {/* کیفیت داده */}
            {stats.data_quality && (
              <Card className="mb-4" style={{ borderRadius: 16 }}>
                <div className="flex items-center gap-2 mb-4">
                  <WarningOutlined className="text-amber-500" />
                  <Title level={5} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                    کیفیت داده
                  </Title>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: stats.data_quality.missing_due > 0 ? '#fee2e2' : '#d1fae5',
                        border: `1px solid ${stats.data_quality.missing_due > 0 ? '#ef444420' : '#10b98120'}`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 11,
                          color: '#6b7280',
                          marginBottom: 4,
                        }}
                      >
                        بدون موعد
                      </div>
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 20,
                          fontWeight: 700,
                          color: stats.data_quality.missing_due > 0 ? '#ef4444' : '#10b981',
                        }}
                      >
                        {stats.data_quality.missing_due || 0}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: stats.data_quality.missing_priority > 0 ? '#fee2e2' : '#d1fae5',
                        border: `1px solid ${stats.data_quality.missing_priority > 0 ? '#ef444420' : '#10b98120'}`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 11,
                          color: '#6b7280',
                          marginBottom: 4,
                        }}
                      >
                        بدون اولویت
                      </div>
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 20,
                          fontWeight: 700,
                          color: stats.data_quality.missing_priority > 0 ? '#ef4444' : '#10b981',
                        }}
                      >
                        {stats.data_quality.missing_priority || 0}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: stats.data_quality.missing_responsible > 0 ? '#fee2e2' : '#d1fae5',
                        border: `1px solid ${stats.data_quality.missing_responsible > 0 ? '#ef444420' : '#10b98120'}`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 11,
                          color: '#6b7280',
                          marginBottom: 4,
                        }}
                      >
                        بدون مسئول
                      </div>
                      <div
                        style={{
                          fontFamily: 'CustomFont',
                          fontSize: 20,
                          fontWeight: 700,
                          color: stats.data_quality.missing_responsible > 0 ? '#ef4444' : '#10b981',
                        }}
                      >
                        {stats.data_quality.missing_responsible || 0}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            )}
          </div>
        )}

        {/* Activity Feed */}
        {activityFeed.length > 0 && (
          <div className="mt-6">
            <Title level={4} style={{ fontFamily: 'CustomFont', marginBottom: 16 }}>
              فید فعالیت
            </Title>
            <Card style={{ borderRadius: 16 }}>
              <div className="space-y-3 max-h-96 overflow-auto">
                {activityFeed.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <InfoCircleOutlined className="text-blue-500" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className="text-sm font-medium text-gray-900 dark:text-white"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {activity.changed_by?.name || 'کاربر'}
                        </span>
                        {activity.type === 'status_change' && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span
                              className="text-xs text-gray-500 dark:text-gray-400"
                              style={{ fontFamily: 'CustomFont' }}
                            >
                              تغییر وضعیت
                            </span>
                            {activity.old_status && activity.new_status && (
                              <>
                                <span className="text-gray-400">:</span>
                                <Tag size="small" style={{ fontFamily: 'CustomFont' }}>
                                  {activity.old_status}
                                </Tag>
                                <span className="text-gray-400">→</span>
                                <Tag size="small" color="blue" style={{ fontFamily: 'CustomFont' }}>
                                  {activity.new_status}
                                </Tag>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      {activity.comment && (
                        <div
                          className="text-xs text-gray-600 dark:text-gray-400 mb-1"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {activity.comment}
                        </div>
                      )}
                      <div
                        className="text-xs text-gray-400 dark:text-gray-500"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {activity.created_at}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {!loading &&
          !stats &&
          (!inbox.needs_acceptance?.length &&
            !inbox.today?.length &&
            !inbox.overdue?.length &&
            !inbox.due_soon?.length) && (
            <div className="mt-8 flex justify-center">
              <Empty
                description={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    داده‌ای برای نمایش در داشبورد یافت نشد
                  </span>
                }
              />
            </div>
          )}
      </div>
    </MainLayout>
  );
};

export default ActionDashboard;

